import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  ConfigProvider,
  DatePicker,
  Drawer,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message
} from "antd";
import {
  DeleteOutlined,
  LockOutlined,
  MessageOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  WarningOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { fetchDay, saveDay } from "./attendanceApi";
import {
  TEMPLATES,
  guessNextType,
  newStamp,
  segmentsToStamps,
  stampsToSegments,
  formatMinutes
} from "./stamps";
import { summarizeDay, validateSegments } from "./timeline";
import { TYPE_META, errorText, warningText } from "./labels";

const { Text } = Typography;
const DATE = "YYYY-MM-DD";

/**
 * Manual entry / correction of one day as a list of time points. Works for any scenario the
 * buttons can't capture afterwards: travel before and after work, several sites, split shifts,
 * night shifts (a time earlier than the one before is the next day), lump-sum breaks.
 */
const DayEditor = ({ open, userId, date: initialDate, projects, onClose, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const [date, setDate] = useState(initialDate);
  const [day, setDay] = useState(null);
  const [stamps, setStamps] = useState([]);
  const [flatBreak, setFlatBreak] = useState(0);
  const [note, setNote] = useState("");
  const [running, setRunning] = useState(false);
  const [notesOpen, setNotesOpen] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (open) setDate(initialDate);
  }, [open, initialDate]);

  useEffect(() => {
    if (!open || !date || !userId) return;
    let cancelled = false;
    setLoading(true);
    setServerErrors([]);
    fetchDay(userId, date)
      .then(res => {
        if (cancelled) return;
        const loaded = res.data;
        setDay(loaded);
        const points = segmentsToStamps(loaded.segments);
        setStamps(points);
        setRunning(loaded.segments.some(s => !s.end));
        setFlatBreak(loaded.flatBreakMinutes || 0);
        setNote(loaded.note || "");
        setNotesOpen({});
        setDirty(false);
      })
      .catch(error => {
        if (!cancelled) message.error(error.response?.data?.message || t("Failed to load the day"));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, date, userId]);

  const today = dayjs().format(DATE);
  const isRecent = date >= dayjs().subtract(1, "day").format(DATE);
  const canRun = isRecent && stamps.length > 0 && stamps[stamps.length - 1].type !== "END";
  const effectiveRunning = running && canRun;

  // Live check with the same rules the server applies
  const computed = useMemo(() => {
    const built = stampsToSegments(stamps, date, { running: effectiveRunning });
    const dayStart = dayjs(date).startOf("day");
    const validation = validateSegments(built.segments, {
      now: Date.now(),
      dayStart: dayStart.valueOf(),
      dayEnd: dayStart.add(1, "day").valueOf(),
      previousEnd: day?.previousEnd ?? null,
      nextStart: day?.nextStart ?? null,
      allowOpen: effectiveRunning
    });
    const rowErrors = {};
    const add = (index, code) => {
      if (index === undefined || index === null) return;
      rowErrors[index] = rowErrors[index] || [];
      if (!rowErrors[index].includes(code)) rowErrors[index].push(code);
    };
    built.errors.forEach(e => add(e.index, e.code));
    validation.forEach(e => add(built.stampOf[e.index], e.code));
    const summary = summarizeDay(built.segments, {
      now: Date.now(),
      flatBreakMinutes: flatBreak,
      targetMinutes: day?.summary?.targetMinutes || 0,
      previousEnd: day?.previousEnd ?? null
    });
    return { ...built, rowErrors, summary, valid: !Object.keys(rowErrors).length };
  }, [stamps, date, effectiveRunning, flatBreak, day]);

  const change = next => {
    setStamps(next);
    setDirty(true);
    setServerErrors([]);
  };
  const update = (index, patch) =>
    change(stamps.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  const remove = index => change(stamps.filter((_, i) => i !== index));
  const insertAfter = index => {
    const next = [...stamps];
    next.splice(index + 1, 0, newStamp(guessNextType(stamps[index])));
    change(next);
  };
  // New points go before a closing END, where they usually belong
  const addPoint = () => {
    const last = stamps[stamps.length - 1];
    if (last?.type === "END") {
      const next = [...stamps];
      next.splice(stamps.length - 1, 0, newStamp(guessNextType(stamps[stamps.length - 2])));
      change(next);
    } else change([...stamps, newStamp(last ? guessNextType(last) : "WORK")]);
  };
  const applyTemplate = template => change(template.types.map(type => newStamp(type)));

  const durationUntilNext = index => {
    const at = computed.segments.findIndex((_, i) => computed.stampOf[i] === index);
    const segment = computed.segments[at];
    if (!segment) return null;
    const end = segment.end || (effectiveRunning ? new Date() : null);
    return end ? (end - segment.start) / 60000 : null;
  };

  const submit = async (segments = computed.segments, extra = {}) => {
    setSaving(true);
    setServerErrors([]);
    try {
      const res = await saveDay(userId, {
        date,
        segments,
        flatBreakMinutes: flatBreak,
        note,
        ...extra
      });
      message.success(t(res.message || "Working times saved"));
      setDirty(false);
      onSaved?.(res.data);
      onClose();
    } catch (error) {
      const data = error.response?.data;
      setServerErrors(data?.errors?.map(e => e.code) || []);
      message.error(data?.message ? t(data.message) : t("Saving failed"));
    } finally {
      setSaving(false);
    }
  };

  const projectOptions = (projects || []).map(p => ({ value: p._id, label: p.projectName }));
  const summary = computed.summary;
  const typeOptions = Object.entries(TYPE_META).map(([value, meta]) => ({
    value,
    label: (
      <span className="attendance-editor__type">
        <span className={`attendance-editor__dot attendance-editor__dot--${value.toLowerCase()}`} />
        {t(meta.label)}
      </span>
    )
  }));

  // Days older than yesterday need the "Attendance-corrections" permission (server decides)
  const readOnly = day?.editable === false;

  const footer = readOnly ? (
    <div className="attendance-editor__footer">
      <span />
      <Button onClick={onClose}>{t("Close")}</Button>
    </div>
  ) : (
    <div className="attendance-editor__footer">
      {day?._id ? (
        <Popconfirm
          title={t("Delete all times of this day?")}
          okText={t("Delete")}
          cancelText={t("Cancel")}
          onConfirm={() => submit([], { flatBreakMinutes: 0, note: "" })}
        >
          <Button danger icon={<DeleteOutlined />} disabled={saving}>
            {isMobile ? null : t("Delete day")}
          </Button>
        </Popconfirm>
      ) : (
        <span />
      )}
      <Space>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button
          type="primary"
          onClick={() => submit()}
          loading={saving}
          disabled={!computed.valid || (!stamps.length && !day?._id)}
        >
          {t("Save")}
        </Button>
      </Space>
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={isMobile ? "100%" : 640}
      rootClassName="attendance-editor"
      destroyOnClose
      title={readOnly ? t("Working times") : t("Enter working times")}
      footer={footer}
    >
      <div className="attendance-editor__head">
        <DatePicker
          value={dayjs(date)}
          format="dd, DD.MM.YYYY"
          allowClear={false}
          disabledDate={d => d.isAfter(dayjs(), "day")}
          onChange={d => d && setDate(d.format(DATE))}
          style={{ width: "100%" }}
        />
        {day?.holiday && <Tag color="purple">{`${t("Holiday")}: ${day.holiday}`}</Tag>}
        {dirty && <Tag color="gold">{t("Unsaved changes")}</Tag>}
      </div>

      <Text type="secondary" className="attendance-editor__help" hidden={readOnly}>
        {t(
          "Enter each time something changes: when you set off, arrive, take a break, leave and get home. Each entry lasts until the next one; “End / off” finishes the day or leaves an unpaid gap."
        )}
      </Text>

      {readOnly && !loading && (
        <Alert
          type="info"
          showIcon
          icon={<LockOutlined />}
          className="attendance-editor__alert"
          message={t("This day is locked")}
          description={t(
            "Only today and yesterday can be changed. Older days need the permission “Attendance corrections”. Ask your manager."
          )}
        />
      )}

      {loading ? (
        <div className="attendance-editor__loading">
          <Spin />
        </div>
      ) : (
        <ConfigProvider componentDisabled={readOnly}>
          {!stamps.length && !readOnly && (
            <div className="attendance-editor__templates">
              <Text strong>
                <ThunderboltOutlined /> {t("Quick start")}
              </Text>
              <div className="attendance-editor__template-list">
                {TEMPLATES.map(template => (
                  <Button key={template.key} onClick={() => applyTemplate(template)}>
                    {t(template.label)}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <ol className="attendance-editor__points">
            {stamps.map((stamp, index) => {
              const errors = computed.rowErrors[index] || [];
              const minutes = durationUntilNext(index);
              const offset = computed.offsets[index] || 0;
              const showNote = notesOpen[stamp.key] || Boolean(stamp.note);
              return (
                <li
                  key={stamp.key}
                  className={`attendance-editor__point attendance-editor__point--${stamp.type.toLowerCase()}${
                    errors.length ? " attendance-editor__point--error" : ""
                  }`}
                >
                  <div className="attendance-editor__row">
                    <Input
                      type="time"
                      value={stamp.time}
                      onChange={e => update(index, { time: e.target.value })}
                      className="attendance-editor__time"
                      aria-label={t("Time")}
                      status={errors.includes("TIME_MISSING") ? "error" : undefined}
                    />
                    <Select
                      value={stamp.type}
                      options={typeOptions}
                      onChange={type => update(index, { type })}
                      className="attendance-editor__select-type"
                      aria-label={t("Type")}
                    />
                    <div className="attendance-editor__row-actions">
                      {stamp.type !== "END" && (
                        <Tooltip title={t("Note")}>
                          <Button
                            type="text"
                            icon={<MessageOutlined />}
                            onClick={() => setNotesOpen({ ...notesOpen, [stamp.key]: !showNote })}
                            aria-label={t("Note")}
                          />
                        </Tooltip>
                      )}
                      <Tooltip title={t("Insert below")}>
                        <Button
                          type="text"
                          icon={<PlusOutlined />}
                          onClick={() => insertAfter(index)}
                          aria-label={t("Insert below")}
                        />
                      </Tooltip>
                      <Tooltip title={t("Remove")}>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(index)}
                          aria-label={t("Remove")}
                        />
                      </Tooltip>
                    </div>
                  </div>

                  {(stamp.type === "WORK" || stamp.type === "TRAVEL") && (
                    <div className="attendance-editor__row attendance-editor__row--details">
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder={t("Project (optional)")}
                        value={stamp.project || undefined}
                        options={projectOptions}
                        onChange={project => update(index, { project: project || null })}
                        className="attendance-editor__project"
                      />
                      {stamp.type === "TRAVEL" && (
                        <Checkbox
                          checked={stamp.paid !== false}
                          onChange={e => update(index, { paid: e.target.checked })}
                        >
                          {t("Counts as working time")}
                        </Checkbox>
                      )}
                    </div>
                  )}

                  {showNote && stamp.type !== "END" && (
                    <Input
                      value={stamp.note}
                      maxLength={500}
                      placeholder={t("Note, e.g. what was done or where")}
                      onChange={e => update(index, { note: e.target.value })}
                      className="attendance-editor__note"
                    />
                  )}

                  <div className="attendance-editor__meta">
                    {offset > 0 && <Tag color="purple">{t("+1 day")}</Tag>}
                    {stamp.source === "CLOCK" && <Tag>{t("Stamped")}</Tag>}
                    {stamp.type !== "END" && minutes !== null && minutes > 0 && (
                      <Text type="secondary">
                        {formatMinutes(minutes)} {t("h")}
                        {stamp.type === "TRAVEL" && stamp.paid === false ? ` · ${t("unpaid")}` : ""}
                      </Text>
                    )}
                    {errors.map(code => (
                      <Text type="danger" key={code}>
                        {errorText(t, code)}
                      </Text>
                    ))}
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="attendance-editor__add" hidden={readOnly}>
            <Button icon={<PlusOutlined />} onClick={addPoint} block>
              {t("Add time point")}
            </Button>
            {stamps.length > 0 && (
              <Button type="link" onClick={() => change([])}>
                {t("Clear")}
              </Button>
            )}
          </div>

          {isRecent && stamps.length > 0 && stamps[stamps.length - 1].type !== "END" && (
            <Checkbox
              checked={running}
              onChange={e => {
                setRunning(e.target.checked);
                setDirty(true);
              }}
              className="attendance-editor__running"
            >
              {date === today
                ? t("Still running (I have not finished yet)")
                : t("Still running (night shift)")}
            </Checkbox>
          )}

          <div className="attendance-editor__extras">
            <label className="attendance-editor__field">
              <Text type="secondary">{t("Lump-sum break (minutes)")}</Text>
              <InputNumber
                min={0}
                max={600}
                step={15}
                value={flatBreak}
                onChange={v => {
                  setFlatBreak(v || 0);
                  setDirty(true);
                }}
                style={{ width: "100%" }}
              />
            </label>
            <label className="attendance-editor__field">
              <Text type="secondary">{t("Note for the day")}</Text>
              <Input.TextArea
                value={note}
                maxLength={1000}
                autoSize={{ minRows: 1, maxRows: 4 }}
                onChange={e => {
                  setNote(e.target.value);
                  setDirty(true);
                }}
              />
            </label>
          </div>

          <div className="attendance-editor__summary">
            <SummaryItem label={t("Work")} value={summary.workMinutes} />
            <SummaryItem label={t("Travel (paid)")} value={summary.travelPaidMinutes} />
            {summary.travelUnpaidMinutes > 0 && (
              <SummaryItem label={t("Travel (unpaid)")} value={summary.travelUnpaidMinutes} />
            )}
            <SummaryItem
              label={t("Breaks")}
              value={summary.breakMinutes + summary.flatBreakMinutes}
            />
            <SummaryItem label={t("Working time")} value={summary.paidMinutes} strong />
            {summary.targetMinutes > 0 && (
              <SummaryItem
                label={t("Balance")}
                value={summary.balanceMinutes}
                signed
                tone={summary.balanceMinutes < 0 ? "danger" : "success"}
              />
            )}
          </div>

          {summary.warnings.length > 0 && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              className="attendance-editor__alert"
              message={t("Please check (Working Hours Act)")}
              description={
                <ul className="attendance-editor__list">
                  {summary.warnings.map(w => (
                    <li key={w.code}>{warningText(t, w)}</li>
                  ))}
                </ul>
              }
            />
          )}
          {serverErrors.length > 0 && (
            <Alert
              type="error"
              showIcon
              className="attendance-editor__alert"
              message={t("The day could not be saved")}
              description={
                <ul className="attendance-editor__list">
                  {[...new Set(serverErrors)].map(code => (
                    <li key={code}>{errorText(t, code)}</li>
                  ))}
                </ul>
              }
            />
          )}
        </ConfigProvider>
      )}
    </Drawer>
  );
};

const SummaryItem = ({ label, value, strong, signed, tone }) => (
  <div className="attendance-editor__summary-item">
    <Text type="secondary">{label}</Text>
    <Text strong={strong} type={tone}>
      {signed && value > 0 ? "+" : ""}
      {formatMinutes(value)}
    </Text>
  </div>
);

export default DayEditor;
